import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { db } from "../../kern/supabase";
import { istLineupStatus, type Act, type LineupStatus } from "./lineuplogik";

/* Daten des Line-ups (Paket E1, E3 und T2). RLS: alle Personen der Kapsel und der Vorstand lesen, schreiben nur über die RPCs aus
   20261015150100_aufgaben_ablage.sql und 20261015160000_aufgaben_timetable.sql (Vorstand mit vorhaben.verwalten und Leitung). Die
   Gage liest die Seite aus finanzen_posten, RLS liefert sie nur Vorstand (finanzen.verwalten) und Leitung. */

const schluessel = (vorhaben: string) => ["vorhaben", "lineup", vorhaben] as const;

export function useLineup(vorhaben: string) {
  return useQuery({
    queryKey: schluessel(vorhaben),
    staleTime: 0,
    queryFn: async (): Promise<Act[]> => {
      const { data, error } = await db()
        .from("aufgaben_vorhaben_lineup")
        .select("id, act, beschreibung, tag, beginn, ende, buehne, status, posten_id, floor_id")
        .eq("vorhaben_id", vorhaben)
        .order("tag", { nullsFirst: true })
        .order("beginn");
      if (error) throw error;
      return (data ?? []).map((a) => ({ ...a, status: istLineupStatus(a.status) ? a.status : "angefragt" }));
    },
  });
}

/** Gage je Posten (Plan in Cent). Nur mit Budgetrecht, sonst fragt die Seite nicht (RLS gäbe ohnehin nichts). Liest die Tabelle des
 *  Moduls finanzen direkt, ohne dessen Code zu importieren. */
export function useGagen(vorhaben: string, darfBudget: boolean) {
  return useQuery({
    queryKey: ["vorhaben", "lineup-gagen", vorhaben],
    enabled: darfBudget,
    staleTime: 0,
    queryFn: async (): Promise<ReadonlyMap<string, number>> => {
      const { data, error } = await db().from("finanzen_posten").select("id, plan_cent").eq("vorhaben_id", vorhaben).eq("kategorie", "kuenstler");
      if (error) throw error;
      return new Map((data ?? []).map((p) => [p.id, p.plan_cent ?? 0]));
    },
  });
}

export interface NeuerAct {
  act: string;
  /** null: Act ohne Slot */
  beginn: string | null;
  ende: string | null;
  tag: string | null;
  floor_id: string | null;
  beschreibung: string;
  status: LineupStatus;
  /** Gage in Cent: legt den Act mit einem Budgetposten „Künstler“ an */
  gage_cent: number | null;
}

export type ActAenderung = Partial<Pick<Act, "act" | "beschreibung" | "tag" | "beginn" | "ende" | "status" | "posten_id" | "floor_id">>;

/** Ein Zug im Timetable: Floor, Tag und Zeiten zusammen. Floor oder Beginn leer heißt „Ohne Slot“. */
export interface Zug {
  id: string;
  floor_id: string | null;
  tag: string | null;
  beginn: string | null;
  ende: string | null;
}

export function useLineupAktionen(vorhaben: string) {
  const qc = useQueryClient();
  // Mit Gage entsteht ein Posten, beim Löschen eines Acts verlieren Unterlagen den Act: Budget und Unterlagen neu laden
  const neu = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: schluessel(vorhaben) }),
      qc.invalidateQueries({ queryKey: ["vorhaben", "lineup-gagen", vorhaben] }),
      qc.invalidateQueries({ queryKey: ["vorhaben", "unterlagen", vorhaben] }),
      qc.invalidateQueries({ queryKey: ["finanzen"] }),
    ]);
  return {
    anlegen: useMutation({
      mutationFn: async (a: NeuerAct): Promise<string> => {
        const gemeinsam = {
          p_vorhaben: vorhaben,
          p_act: a.act,
          // Ohne Beginn: Act ohne Slot. Die Typen aus der Datenbank kennen kein null für Parameter, die Funktion nimmt es an.
          p_beginn: a.beginn as string,
          p_ende: a.ende ?? undefined,
          p_tag: a.tag ?? undefined,
          p_beschreibung: a.beschreibung,
          p_status: a.status,
        };
        const { data, error } =
          a.gage_cent !== null
            ? await db().rpc("aufgaben_vorhaben_lineup_mit_gage_anlegen", { ...gemeinsam, p_gage_cent: a.gage_cent })
            : await db().rpc("aufgaben_vorhaben_lineup_anlegen", gemeinsam);
        if (error) throw error;
        const id = String(data);
        // Anlegen kennt keinen Floor (gleiche Signatur wie in E1, ADR-019), den setzt das Bearbeiten
        if (a.floor_id) {
          const b = await db().rpc("aufgaben_vorhaben_lineup_bearbeiten", { p_id: id, p_aenderung: { floor_id: a.floor_id } });
          if (b.error) throw b.error;
        }
        return id;
      },
      onSettled: neu,
    }),
    bearbeiten: useMutation({
      mutationFn: async ({ id, aenderung }: { id: string; aenderung: ActAenderung }) => {
        const { error } = await db().rpc("aufgaben_vorhaben_lineup_bearbeiten", { p_id: id, p_aenderung: aenderung });
        if (error) throw error;
      },
      onSettled: neu,
    }),
    /** Zug im Timetable: die Ansicht ändert sich sofort, bei einem Fehler springt der Act zurück */
    slot: useMutation({
      mutationFn: async (z: Zug) => {
        const { error } = await db().rpc("aufgaben_vorhaben_lineup_slot", {
          p_id: z.id,
          // null heißt „Ohne Slot“, die Typen aus der Datenbank kennen kein null für Parameter
          p_floor_id: z.floor_id as string,
          p_tag: z.tag as string,
          p_beginn: z.beginn as string,
          p_ende: z.ende as string,
        });
        if (error) throw error;
      },
      onMutate: async (z: Zug) => {
        await qc.cancelQueries({ queryKey: schluessel(vorhaben) });
        const vorher = qc.getQueryData<Act[]>(schluessel(vorhaben));
        const ohne = !z.floor_id || !z.beginn;
        qc.setQueryData<Act[]>(schluessel(vorhaben), (alt) =>
          alt?.map((a) =>
            a.id === z.id ? { ...a, floor_id: ohne ? null : z.floor_id, tag: z.tag, beginn: ohne ? null : z.beginn, ende: ohne ? null : z.ende } : a,
          ),
        );
        return { vorher };
      },
      onError: (_e, _z, k) => {
        if (k?.vorher) qc.setQueryData(schluessel(vorhaben), k.vorher);
      },
      onSettled: () => qc.invalidateQueries({ queryKey: schluessel(vorhaben) }),
    }),
    /** Statuswahl: die Pille zeigt den neuen Status sofort, bei einem Fehler springt sie zurück */
    status: useMutation({
      mutationFn: async ({ id, status }: { id: string; status: LineupStatus }) => {
        const { error } = await db().rpc("aufgaben_vorhaben_lineup_status", { p_id: id, p_status: status });
        if (error) throw error;
      },
      onMutate: async ({ id, status }: { id: string; status: LineupStatus }) => {
        await qc.cancelQueries({ queryKey: schluessel(vorhaben) });
        const vorher = qc.getQueryData<Act[]>(schluessel(vorhaben));
        qc.setQueryData<Act[]>(schluessel(vorhaben), (alt) => alt?.map((a) => (a.id === id ? { ...a, status } : a)));
        return { vorher };
      },
      onError: (_e, _z, k) => {
        if (k?.vorher) qc.setQueryData(schluessel(vorhaben), k.vorher);
      },
      onSettled: neu,
    }),
    loeschen: useMutation({
      mutationFn: async (id: string) => {
        const { error } = await db().rpc("aufgaben_vorhaben_lineup_loeschen", { p_id: id });
        if (error) throw error;
      },
      onSettled: neu,
    }),
  };
}
